// ORCA360 - Audio Upload Utility
// Handles chunked upload of recorded audio to Supabase Storage
// Storage bucket: session-recordings
// Path structure: /{session_id}/{phase}/chunk-NNNN.webm + complete.webm

import { supabase } from './supabase';
import { uploadWithTus, shouldUseTus, type TusProgressInfo } from './tusUpload';

const STORAGE_BUCKET = 'session-recordings';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export type RecordingPhase = 'briefing' | 'simulator' | 'debriefing';
/** Extended phase type that includes concatenated phases */
export type ConcatenatedPhase = 'briefing_sim' | 'full';

interface UploadChunkParams {
  chunk: Blob;
  index: number;
  sessionId: string;
  phase: RecordingPhase;
}

interface UploadCompleteParams {
  blob: Blob;
  sessionId: string;
  phase: RecordingPhase;
  durationSeconds: number;
  chunkCount: number;
  /** Optional progress callback for TUS uploads */
  onProgress?: (info: TusProgressInfo) => void;
}

interface UploadCompleteResult {
  storagePath: string;
  signedUrl: string;
  recordingId: string;
}

/**
 * Retry wrapper for async operations
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  retries = MAX_RETRIES,
  delayMs = RETRY_DELAY_MS
): Promise<T> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err as Error;
      if (attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

/**
 * Upload a single audio chunk during recording.
 * Chunks are stored at /{session_id}/{phase}/chunk-{NNNN}.webm
 */
export async function uploadAudioChunk({
  chunk,
  index,
  sessionId,
  phase,
}: UploadChunkParams): Promise<void> {
  const paddedIndex = String(index).padStart(4, '0');
  const path = `${sessionId}/${phase}/chunk-${paddedIndex}.webm`;

  await withRetry(async () => {
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(path, chunk, {
        contentType: 'audio/webm;codecs=opus',
        upsert: true,
      });

    if (error) {
      throw new Error(`Chunk upload failed: ${error.message}`);
    }
  });
}

/**
 * Upload the complete recording after stopping, generate a signed URL for
 * AssemblyAI, and upsert metadata to session_recordings table.
 *
 * Uses TUS resumable upload for blobs >= 6MB (most recordings).
 * Falls back to standard upload with retry for smaller blobs.
 */
export async function uploadCompleteRecording({
  blob,
  sessionId,
  phase,
  durationSeconds,
  chunkCount,
  onProgress,
}: UploadCompleteParams): Promise<UploadCompleteResult> {
  const storagePath = `${sessionId}/${phase}/complete.webm`;

  // 1. Upload complete file — use TUS for large blobs (most recordings)
  if (shouldUseTus(blob.size)) {
    console.log(
      `[audioUpload] Using TUS for complete recording (${(blob.size / (1024 * 1024)).toFixed(1)}MB)`
    );
    await uploadWithTus(blob, {
      bucket: STORAGE_BUCKET,
      objectPath: storagePath,
      contentType: 'audio/webm;codecs=opus',
      upsert: true,
      onProgress,
    });
  } else {
    await withRetry(async () => {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(storagePath, blob, {
          contentType: 'audio/webm;codecs=opus',
          upsert: true,
        });

      if (error) {
        throw new Error(`Complete recording upload failed: ${error.message}`);
      }
    });
  }

  // 2. Generate signed URL (2 hour expiry for transcription queue delays)
  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 7200);

  if (signedUrlError || !signedUrlData?.signedUrl) {
    throw new Error(`Failed to create signed URL: ${signedUrlError?.message || 'No URL returned'}`);
  }

  // 3. Upsert recording metadata
  const { data: recording, error: recordingError } = await supabase
    .from('session_recordings')
    .upsert(
      {
        session_id: sessionId,
        phase,
        storage_path: storagePath,
        duration_seconds: durationSeconds,
        mime_type: 'audio/webm;codecs=opus',
        file_size_bytes: blob.size,
        chunk_count: chunkCount,
        status: 'uploaded',
      },
      { onConflict: 'session_id,phase' }
    )
    .select('id')
    .single();

  if (recordingError || !recording) {
    throw new Error(`Failed to save recording metadata: ${recordingError?.message || 'No data'}`);
  }

  return {
    storagePath,
    signedUrl: signedUrlData.signedUrl,
    recordingId: recording.id,
  };
}

/**
 * Get a signed URL for an existing recording.
 */
export async function getRecordingSignedUrl(
  sessionId: string,
  phase: RecordingPhase
): Promise<string> {
  const storagePath = `${sessionId}/${phase}/complete.webm`;

  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 3600);

  if (error || !data?.signedUrl) {
    throw new Error(`Failed to get signed URL: ${error?.message || 'No URL returned'}`);
  }

  return data.signedUrl;
}

/**
 * Delete chunk files after transcription succeeds.
 */
export async function cleanupChunks(
  sessionId: string,
  phase: RecordingPhase,
  chunkCount: number
): Promise<void> {
  const paths: string[] = [];
  for (let i = 0; i < chunkCount; i++) {
    const paddedIndex = String(i).padStart(4, '0');
    paths.push(`${sessionId}/${phase}/chunk-${paddedIndex}.webm`);
  }

  if (paths.length === 0) return;

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .remove(paths);

  if (error) {
    console.warn(`[audioUpload] Failed to cleanup chunks: ${error.message}`);
  }
}

/**
 * Register a recording in the session_recordings table without uploading a complete blob.
 * Used by the "Record More" flow where chunks are the source of truth.
 * Returns the recording ID.
 */
export async function registerRecording(params: {
  sessionId: string;
  phase: RecordingPhase;
  durationSeconds: number;
  chunkCount: number;
}): Promise<string> {
  const { sessionId, phase, durationSeconds, chunkCount } = params;
  const storagePath = `${sessionId}/${phase}/complete.webm`;

  const { data: recording, error: recordingError } = await supabase
    .from('session_recordings')
    .upsert(
      {
        session_id: sessionId,
        phase,
        storage_path: storagePath,
        duration_seconds: durationSeconds,
        mime_type: 'audio/webm;codecs=opus',
        chunk_count: chunkCount,
        status: 'uploaded',
      },
      { onConflict: 'session_id,phase' }
    )
    .select('id')
    .single();

  if (recordingError || !recording) {
    throw new Error(`Failed to register recording: ${recordingError?.message || 'No data'}`);
  }

  return recording.id;
}

/**
 * Trigger the Inngest transcription function for a recording.
 * Sends the session/recording.completed event via API.
 */
export async function triggerTranscription(
  sessionId: string,
  phase: RecordingPhase | ConcatenatedPhase,
  audioUrl: string,
  recordingId: string,
  language?: string
): Promise<void> {
  const response = await fetch('/api/trigger-recording-transcription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      phase,
      audioUrl,
      recordingId,
      language,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Transcription trigger failed: ${response.status}`);
  }
}

/**
 * Concatenate per-phase recordings into a single MP3 file via the FFmpeg microservice.
 * Returns the new recording ID and a signed URL for the concatenated file.
 *
 * @param sessionId - Session ID
 * @param outputPhase - Target phase: 'briefing_sim' (briefing+sim) or 'full' (all phases)
 */
export async function concatenateRecordings(
  sessionId: string,
  outputPhase: ConcatenatedPhase
): Promise<{ recordingId: string; signedUrl: string; storagePath: string }> {
  const clientDatetime = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(':', '-');

  const response = await fetch('/api/concatenate-recordings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      outputPhase,
      clientDatetime,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(
      errorData.error || `Concatenation failed: ${response.status}`
    );
  }

  const result = await response.json();
  return {
    recordingId: result.recordingId,
    signedUrl: result.signedUrl,
    storagePath: result.storagePath,
  };
}
